import {customElement, property} from 'lit/decorators.js';
import type {Model} from './architecture.js';
import {Msg, main} from './architecture.js';
import type {Constructor} from '@gl/lit/types/classes.js';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import {ImmutableComponent} from '@gl/lit/components/mixins/immutable_component/index.js';
import {isDateInputString, isHourAndMinuteString} from '@gl/lit/utils/dates.js';
import {isKnownTimezone} from '@gl/lit/common/constants/timezones.js';

import styles from './style.scss';
import selectStyles from '@gl/lit/components/base/select/style.scss';
import type {
  Nullable,
  DateInputString,
  HourAndMinuteString,
} from '@gl/lit/types/general.js';
import type {Timezone, TimezoneData} from '@gl/lit/types/timezone.js';
import {isString} from '@gl/lit/utils/index.js';

export type ScheduledData = {
  scheduled: true;
  date: DateInputString;
  time: HourAndMinuteString;
  timezone: TimezoneData;
};

export type NotScheduledData = {
  scheduled: false;
  date: null;
  time: null;
  timezone: null;
};

export type ValidSchedule = ScheduledData | NotScheduledData;

export const SCHEDULE_DATE_ELEMENT_TAG = 'fly-schedule-date';

/**
 * Handle frequency scheduling with the recurring defaults.
 *
 * @fires FlyScheduleDateInput#scheduledate:change
 * @fires FlyScheduleDateInput#scheduledate:change:schedule
 * @fires FlyScheduleDateInput#scheduledate:change:date
 * @fires FlyScheduleDateInput#scheduledate:change:time
 * @fires FlyScheduleDateInput#scheduledate:change:timezone
 * @fires FlyScheduleDateInput#scheduledate:change:valid
 */
@customElement(SCHEDULE_DATE_ELEMENT_TAG)
export default class FlyScheduleDateInput extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  @property({type: String})
  form = '';

  @property({type: String})
  name = '';

  @property({type: String})
  get label() {
    return this.model.label;
  }
  set label(value) {
    this.handleUpdate(Msg.setLabel(value ?? ''));
  }

  @property({
    type: String,
    attribute: 'no-schedule-label',
  })
  noScheduleLabel = 'Now';

  @property({type: Boolean})
  get scheduled() {
    return this.model.scheduled;
  }
  set scheduled(value) {
    this.handleUpdate(Msg.setScheduled(value));
  }

  @property({
    type: String,
    converter(value) {
      return isDateInputString(value) ? value : '';
    },
  })
  get date() {
    return this.model.date;
  }
  set date(value) {
    this.handleUpdate(Msg.setDate(value));
  }

  @property({
    type: String,
    converter(value) {
      return isHourAndMinuteString(value) ? value : '';
    },
  })
  get time() {
    return this.model.time;
  }
  set time(value) {
    this.handleUpdate(Msg.setTime(value));
  }

  @property({
    type: String,
    converter: {
      fromAttribute: value => {
        return isString(value) && isKnownTimezone(value) ? value : '';
      },
      toAttribute: (timezone: Nullable<Timezone>) => {
        return timezone?.name ?? '';
      },
    },
  })
  get timezone() {
    return this.model.timezone?.name ?? '';
  }
  set timezone(value) {
    this.handleUpdate(Msg.setTimezone(value));
  }

  @property({type: Boolean, reflect: true})
  get hidden() {
    return this.model.hidden;
  }
  set hidden(value) {
    this.handleUpdate(Msg.setHidden(value));
  }

  @property({type: Boolean, reflect: true})
  get required() {
    return this.model.required;
  }
  set required(value) {
    this.handleUpdate(Msg.setHidden(value));
  }

  @property({type: Boolean, reflect: true})
  get disabled() {
    return this.model.disabled;
  }
  set disabled(value) {
    this.handleUpdate(Msg.setDisabled(value));
  }

  get internals() {
    return this._internals;
  }

  private _internals = this.attachInternals();

  static get formAssociated() {
    return true;
  }

  static get styles() {
    return [styles, selectStyles];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [SCHEDULE_DATE_ELEMENT_TAG]: FlyScheduleDateInput;
  }

  interface HTMLElementEventMap {
    'scheduledate:change': CustomEvent<{
      scheduled: boolean;
      date: Nullable<DateInputString>;
      time: Nullable<HourAndMinuteString>;
      timezone: Nullable<TimezoneData>;
      valid: boolean;
    }>;
    'scheduledate:change:schedule': CustomEvent<{
      value: boolean;
    }>;
    'scheduledate:change:date': CustomEvent<{
      value: Nullable<DateInputString>;
    }>;
    'scheduledate:change:time': CustomEvent<{
      value: Nullable<HourAndMinuteString>;
      timezone: Nullable<TimezoneData>;
    }>;
    'scheduledate:change:timezone': CustomEvent<{
      value: Nullable<TimezoneData>;
    }>;
    'scheduledate:change:valid': CustomEvent<ValidSchedule>;
  }
}
